import scrollLock from 'scroll-lock';
import anime from 'animejs/lib/anime.es.js';
import Splitting from 'splitting';

export default class ChapterIndex {
	static create(container, chapter) {
		const chapterIndexElement = container.querySelector('[data-chapter-index]');
		if (!chapterIndexElement) {
			return null;
		}

		return new ChapterIndex(chapterIndexElement, chapter);
	}

	constructor(el, chapter) {
		this.el = el;
		this.chapter = false;
		if (chapter) {
			this.chapter = chapter;
		}

		this.breakpoint = window.matchMedia('(max-width:991px)');

		// Scrolling Elements
		this.scrollElm = this.el.querySelector('[data-chapter-index-scroll]');
		this.scrollElmCredits = this.el.querySelector('[data-chapter-index-credits-scroll]');

		this.header = this.el.querySelector('[pllntrs-chapter-index-header]');
		this.headerScaler = this.el.querySelector('[data-chapter-index-header-scaler]');

		this.indexPage = this.el.closest('[data-chapter-index-page]');
		if (this.indexPage) {
			this.backgroundElement = this.indexPage.querySelector('[data-index-bg-image]').querySelector('img');
			this.backgroundElementInner = this.indexPage.querySelector('[data-index-bg-image-inner]');
			anime.set(this.backgroundElementInner, {
				scale: 1.3
			});

			// Header Logo
			this.headerLogo = this.indexPage.querySelector('[data-chapter-index-header-logo]');
			anime.set(this.headerLogo, {
				opacity: 0,
			});

			this.scrollElm.style.overflow = 'hidden';
		}

		this.creditsBackgroundElement = this.el.querySelector('[data-index-credits-bg-image]').querySelector('img');
		anime.set(this.creditsBackgroundElement, {
			translateY: -250,
		});
		this.creditsBackgroundElementImg = this.el.querySelector('[data-index-credits-bg-image]');

		this.title = this.el.querySelector('[data-chapter-index-title]');
		if (this.indexPage) {
			this.splitTitle = Splitting({
				target: this.title,
				by: 'chars',
			});
			anime.set(this.splitTitle[0].chars, {
				opacity: 0,
			});
		} else {
			anime.set(this.title, {
				opacity: 0,
			});
		}

		this.subheading = this.el.querySelector('[data-chapter-index-subheading]');
		if (this.indexPage) {
			this.splitSubheading = Splitting({
				target: this.subheading,
				by: 'chars',
			});
			anime.set(this.splitSubheading[0].chars, {
				opacity: 0,
			});
		} else {
			anime.set(this.subheading, {
				opacity: 0,
			});
		}

		this.copyright = this.el.querySelector('[data-chapter-index-copyright]');
		if (this.indexPage) {
			this.splitCopyright = Splitting({
				target: this.copyright,
				by: 'chars',
			});
			anime.set(this.splitCopyright[0].chars, {
				opacity: 0,
			});
		} else {
			anime.set(this.copyright, {
				opacity: 0,
			});
		}

		this.dot = this.el.querySelector('[data-chapter-index-dot]');

		// Chapters
		this.chaptersElement = this.el.querySelector('[data-chapter-index-chapters]');
		this.chapterCards = this.chaptersElement.querySelectorAll('a');

		// Credits
		this.creditsElement = this.el.querySelector('[data-chapter-credits]');
		this.creditsContentElement = this.el.querySelector('[data-chapter-credits-content]');
		this.creditsButtonElement = this.el.querySelector('[data-credits-button]');
		anime.set(this.creditsButtonElement, {
			opacity: 0,
		});
		this.creditsCloseButtonElement = this.el.querySelector('[data-chapter-credits-close]');
		this.creditsButtonElement.addEventListener('click', e => {
			this.showCredits();
		});
		this.creditsCloseButtonElement.addEventListener('click', e => {
			this.hideCredits();
		});

		this.bigCloseButton = this.el.querySelector('[data-big-close-button]');
		if(this.chapter) {
			this.bigCloseButton.addEventListener('click', e => {
				this.close();
			});
		} else {
			this.bigCloseButton.remove();
		}

		// Scroll Listener
		this.boundScrollListener = this.handleScroll.bind(this);
		this.scrollElm.addEventListener('scroll', this.boundScrollListener);
		this.handleScroll();

		// Scroll Listener
		this.boundCreditsScrollListener = this.handleCreditsScroll.bind(this);
		this.scrollElmCredits.addEventListener('scroll', this.boundCreditsScrollListener);
		this.handleCreditsScroll();

		if (this.indexPage) {
			setTimeout(() => {
				this.bringIntoFocus();
			}, 0);
		} else {
			this.isOpen = false;
		}

	}

	handleCreditsScroll() {
		const creditsScroll = this.scrollElmCredits.scrollTop;

		if (prevCreditsScroll != parseInt(creditsScroll / -4)) {
			anime.set(this.creditsBackgroundElementImg, {
				translateY: parseInt(creditsScroll / -4) + 'px',
			});
		}
		let prevCreditsScroll = parseInt(creditsScroll / -4);
	}

	handleScroll() {
		const indexScroll = this.scrollElm.scrollTop;

		let chapterCardHeight = 150;
		if (this.chapterCards.length > 0) {
			chapterCardHeight = this.chapterCards[0].scrollHeight;
		}

		const currentCard = Math.round(indexScroll / chapterCardHeight);
		this.chapterCards.forEach(card => {
			card.classList.remove('is-active');
		});
		if (currentCard) {
			this.chapterCards[currentCard].classList.add('is-active');
		} else {
			this.chapterCards[0].classList.add('is-active');
		}

		if (indexScroll > 15) { // 15 is trivial, just be just a little, but more than 7..
			this.el.classList.add('header-hidden');
		} else {
			this.el.classList.remove('header-hidden');
		}

		if (prevIndexScroll != parseInt(indexScroll / -3)) {
			anime.set(this.backgroundElement, {
				translateY: parseInt(indexScroll / -3) + 'px',
			});
		}
		let prevIndexScroll = parseInt(indexScroll / -3);
	}

	toggle() {
		if (this.isOpen) {
			this.close();
		} else {
			this.open();
		}
	}

	open() {
		if (this.isAnimating) {
			return;
		}

		this.chapter.chapterIndexButton.classList.remove('show-open');

		const tl = anime.timeline({
			begin: () => {
				this.isAnimating = true;
				this.el.classList.add('is-shown');
				scrollLock.disablePageScroll(this.scrollElm);
				if (this.chapter) {
					this.chapter.elm.classList.add('index-shown');
				}

				if (!this.indexPage) {
					this.chapterCards.forEach((elm, index) => {
						if (elm.classList.contains('c-chapters-list-card__link--current')) {
							const chapterCardHeight = this.chapterCards[0].scrollHeight;
							this.scrollElm.scrollTo({
								top: index * chapterCardHeight,
							});
						}
					});
				}
			},
			complete: () => {
				this.isAnimating = false;
				this.isOpen = true;
			},
		});

		tl.add(
			{
				targets: this.title,
				opacity: 1,
				duration: 600,
				easing: 'linear',
			},
			500,
		);
		tl.add(
			{
				targets: this.subheading,
				opacity: 1,
				duration: 600,
				easing: 'linear',
			},
			800,
		);
		tl.add(
			{
				targets: this.copyright,
				opacity: 1,
				duration: 600,
				easing: 'linear',
			},
			1100,
		);
		tl.add(
			{
				targets: this.creditsButtonElement,
				opacity: 1,
				duration: 1000,
				easing: 'linear',
			},
			1100,
		);
		tl.add(
			{
				targets: this.dot,
				opacity: 1,
				duration: 600,
				easing: 'linear',
			},
			1500,
		);
		tl.add(
			{
				targets: this.chapterCards,
				translateX: ['120%', '0%'],
				duration: 600,
				easing: 'easeOutSine',
				delay: anime.stagger(150),
			},
			0,
		);
	}

	close() {
		if (this.isAnimating) {
			return;
		}

		this.chapter.chapterIndexButton.classList.add('show-open');

		const tl = anime.timeline({
			begin: () => {
				this.isAnimating = true;
			},
			complete: () => {
				this.isOpen = false;
				this.isAnimating = false;
				this.el.classList.remove('is-shown');
				if (this.chapter) {
					this.chapter.elm.classList.remove('index-shown');
				}
				scrollLock.clearQueueScrollLocks();
				scrollLock.enablePageScroll(this.scrollElm);
			},
		});

		tl.add(
			{
				targets: this.title,
				opacity: 0,
				duration: 600,
				easing: 'linear',
			},
			0,
		);
		tl.add(
			{
				targets: this.subheading,
				opacity: 0,
				duration: 600,
				easing: 'linear',
			},
			0,
		);
		tl.add(
			{
				targets: this.copyright,
				opacity: 0,
				duration: 600,
				easing: 'linear',
			},
			0,
		);
		tl.add(
			{
				targets: this.creditsButtonElement,
				opacity: 0,
				duration: 1000,
				easing: 'linear',
			},
			0,
		);
		tl.add(
			{
				targets: this.dot,
				opacity: 0,
				duration: 600,
				easing: 'linear',
			},
			0,
		);
		tl.add(
			{
				targets: this.chapterCards,
				translateX: ['0%', '120%'],
				duration: 600,
				easing: 'easeInSine',
				delay: anime.stagger(100, { direction: 'reverse' }),
			},
			0,
		);
	}

	bringIntoFocus() {
		const tl = anime.timeline({
			begin: () => {
				this.indexPage.classList.add('is-shown');
			},
		});

		anime({
			targets: this.backgroundElementInner,
			scale: 1,
			duration: 9000,
			easing: 'easeInOutSine',
			delay: 2000
		});

		tl.add(
			{
				targets: this.headerLogo,
				opacity: 1,
				duration: 1000,
				easing: 'linear',
			},
			3500,
		);

		tl.add(
			{
				targets: this.splitTitle[0].chars,
				opacity: 1,
				duration: 1,
				easing: 'linear',
				delay: anime.stagger(100),
			},
			5000,
		);
		tl.add({
			targets: this.splitSubheading[0].chars,
			opacity: 1,
			duration: 1,
			easing: 'linear',
			delay: anime.stagger(50),
		});
		tl.add({
			targets: this.splitCopyright[0].chars,
			opacity: 1,
			duration: 1,
			easing: 'linear',
			delay: anime.stagger(50),
			begin: () => {
				anime({
					targets: this.creditsButtonElement,
					opacity: 1,
					duration: 1000,
					easing: 'linear',
				});
			},
		});
		tl.add({
			targets: this.dot,
			opacity: 1,
			duration: 1000,
			easing: 'linear',
		});

		tl.add(
			{
				targets: this.chapterCards,
				translateX: ['120%', '0%'],
				duration: 1000,
				easing: 'easeOutSine',
				delay: anime.stagger(150),
				begin: () => {
					this.scrollElm.style.overflow = 'auto';
				},
			},
			6500,
		);
	}

	showCredits() {
		const tl = anime.timeline({
			begin: () => {
				this.el.classList.add('credits-shown');
				if (this.chapter) {
					this.chapter.elm.classList.add('credits-shown');
				}
				this.creditsButtonElement.classList.add('is-hidden');
				this.creditsElement.classList.add('is-active');

				scrollLock.disablePageScroll(this.scrollElmCredits);
			},
		});

		if (this.indexPage) {
			tl.add(
				{
					targets: this.backgroundElementInner,
					opacity: 0,
					translateY: -250,
					duration: 2000,
					easing: 'easeInOutSine',
				},
				0,
			);
		}
		tl.add(
			{
				targets: this.chapterCards,
				translateX: ['0%', '120%'],
				duration: 600,
				easing: 'easeInSine',
				delay: anime.stagger(100, { direction: 'reverse' }),
			},
			0,
		);
		tl.add(
			{
				targets: this.dot,
				opacity: 0,
				duration: 600,
				easing: 'linear',
			},
			0,
		);
		tl.add(
			{
				targets: this.creditsButtonElement,
				opacity: 0,
				duration: 600,
				easing: 'linear',
			},
			0,
		);

		if (this.breakpoint.matches === false) {
			// Desktop

			tl.add(
				{
					targets: this.headerScaler,
					scale: 0.8,
					duration: 1000,
					easing: 'easeInOutCubic',
				},
				0,
			);
			tl.add(
				{
					targets: this.header,
					minHeight: this.headerScaler.clientHeight + 'px',
					duration: 1200,
					easing: 'easeInOutCubic',
					complete: () => {
						this.header.style.height = 'unset';
					},
				},
				800,
			);
			tl.add(
				{
					targets: this.creditsContentElement,
					opacity: 1,
					duration: 1200,
					easing: 'easeInOutCubic',
				},
				800,
			);
			if (this.indexPage) {
				tl.add(
					{
						targets: this.creditsBackgroundElement,
						opacity: 1,
						translateY: 0,
						duration: 1600,
						easing: 'easeInOutSine',
					},
					400,
				);
			}
		} else {
			// Mobile

			tl.add(
				{
					targets: this.creditsContentElement,
					opacity: 1,
					duration: 1200,
					easing: 'easeInOutCubic',
				},
				0,
			);

			if (this.indexPage) {
				tl.add(
					{
						targets: this.creditsBackgroundElement,
						opacity: 1,
						translateY: 0,
						duration: 2000,
						easing: 'easeInOutCubic',
					},
					0,
				);
			}
		}
	}

	hideCredits() {
		const tl = anime.timeline({
			begin: () => {
				this.el.classList.remove('credits-shown');
				if (this.chapter) {
					this.chapter.elm.classList.remove('credits-shown');
				}
				this.creditsButtonElement.classList.remove('is-hidden');
				this.creditsElement.classList.remove('is-active');

				this.scrollElmCredits.scrollTo({
					top: 0,
					left: 0,
					behavior: 'smooth',
				});

				scrollLock.disablePageScroll(this.scrollElm);
			},
		});

		if (this.breakpoint.matches === false) {
			// Desktop

			tl.add(
				{
					targets: this.header,
					minHeight: [this.headerScaler.clientHeight + 'px', window.innerHeight * 0.5 + 'px'],
					duration: 1200,
					easing: 'easeInOutCubic',
					complete: () => {
						this.header.style.removeProperty('height');
					},
				},
				0,
			);
			tl.add(
				{
					targets: this.creditsContentElement,
					opacity: 0,
					duration: 1200,
					easing: 'easeInOutCubic',
				},
				0,
			);
			tl.add(
				{
					targets: this.headerScaler,
					scale: 1,
					duration: 1000,
					easing: 'easeInOutCubic',
				},
				1200,
			);
			if (this.indexPage) {
				tl.add(
					{
						targets: this.creditsBackgroundElement,
						opacity: 0,
						translateY: -250,
						duration: 2000,
						easing: 'easeInOutSine',
					},
					0,
				);
			}
		} else {
			// Mobile

			if (this.indexPage) {
				tl.add(
					{
						targets: this.creditsBackgroundElement,
						opacity: 0,
						translateY: -250,
						duration: 2000,
						easing: 'easeInOutCubic',
					},
					0,
				);
			}
			tl.add(
				{
					targets: this.creditsContentElement,
					opacity: 0,
					duration: 1200,
					easing: 'easeInOutCubic',
				},
				0,
			);
		}

		tl.add(
			{
				targets: this.creditsButtonElement,
				opacity: 1,
				duration: 600,
				easing: 'linear',
			},
			2000,
		);
		tl.add(
			{
				targets: this.dot,
				opacity: 1,
				duration: 600,
				easing: 'linear',
			},
			2400,
		);
		tl.add(
			{
				targets: this.chapterCards,
				translateX: ['120%', '0%'],
				duration: 600,
				easing: 'easeOutSine',
				delay: anime.stagger(150),
			},
			1200,
		);
		tl.add(
			{
				targets: this.backgroundElementInner,
				opacity: 1,
				translateY: 0,
				duration: 2400,
				easing: 'easeInOutSine',
			},
			0,
		);
	}
}
